import { DeviceType } from '../constants/deviceTypes';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Paths, File } from 'expo-file-system';
import { Platform } from 'react-native';
import logger from '../utils/logger';
import {
  generateReportTemplate,
  generateReportData,
  formatMetricValue,
  getMetricLabel,
} from '../utils/reportTemplateEngine';
import type { DeviceTypeConfig } from '../types';

interface DeviceData {
  id: string;
  name: string;
  deviceType: DeviceType;
  site?: string;
  zone?: string;
  hasBattery?: boolean;
  status?: string;
  lastSeenAt?: Date;
}

interface TelemetryDataPoint {
  timestamp: Date;
  temperature?: number;
  humidity?: number;
  level?: number;
  battery?: number;
  rssi?: number;
  pressure?: number;
  pressureDiffPa?: number;
  light?: number;
  co2?: number;
  pm25?: number;
}

interface ReportOptions {
  includeCharts: boolean;
  includeStatistics: boolean;
  includeRawData: boolean;
  dateRange: string;
  startDate?: Date;
  endDate?: Date;
}

interface Statistics {
  min: number;
  max: number;
  avg: number;
  count: number;
}

class ReportGeneratorService {
  /**
   * Generate device report based on type
   */
  async generateDeviceReport(
    device: DeviceData,
    telemetryData: TelemetryDataPoint[],
    options: ReportOptions,
    organizationName: string,
    language: 'ar' | 'en' = 'en',
    logoBase64?: string,
  ): Promise<string> {
    const isArabic = language === 'ar';

    // Calculate statistics
    const stats = this.calculateStatistics(telemetryData, device.deviceType);

    // Generate HTML content
    const html = this.generateHTML(
      device,
      telemetryData,
      stats,
      options,
      organizationName,
      isArabic,
      logoBase64,
    );

    try {
      // Generate PDF
      const { uri } = await Print.printToFileAsync({
        html,
        base64: false,
      });

      return uri;
    } catch (error) {
      logger.error('Failed to generate PDF:', error);
      throw error;
    }
  }

  /**
   * Generate comparison report for multiple devices
   */
  async generateComparisonReport(
    devices: DeviceData[],
    telemetryDataMap: Map<string, TelemetryDataPoint[]>,
    options: ReportOptions,
    organizationName: string,
    language: 'ar' | 'en' = 'en',
  ): Promise<string> {
    const isArabic = language === 'ar';

    const html = this.generateComparisonHTML(
      devices,
      telemetryDataMap,
      options,
      organizationName,
      isArabic,
    );

    try {
      const { uri } = await Print.printToFileAsync({
        html,
        base64: false,
      });

      return uri;
    } catch (error) {
      logger.error('Failed to generate comparison PDF:', error);
      throw error;
    }
  }

  /**
   * Generate dynamic device report using template engine
   * This method uses the device type configuration from the database
   * to generate reports without hardcoded device type logic
   */
  async generateDynamicDeviceReport(
    device: DeviceData & { capabilities?: Record<string, any> },
    deviceTypeConfig: DeviceTypeConfig,
    telemetryData: TelemetryDataPoint[],
    alarmEvents: any[],
    options: ReportOptions,
    organizationName: string,
    language: 'ar' | 'en' = 'en',
  ): Promise<string> {
    const isArabic = language === 'ar';

    try {
      // Generate report template based on device type config
      const template = generateReportTemplate(deviceTypeConfig);

      // Generate report data with aggregations
      const reportData = await generateReportData(device.id, template, telemetryData, alarmEvents);

      // Generate HTML using dynamic template
      const html = this.generateDynamicHTML(
        device,
        deviceTypeConfig,
        reportData,
        options,
        organizationName,
        isArabic,
      );

      // Generate PDF
      const { uri } = await Print.printToFileAsync({
        html,
        base64: false,
      });

      return uri;
    } catch (error) {
      logger.error('Failed to generate dynamic PDF:', error);
      throw error;
    }
  }

  /**
   * Export data to CSV format
   */
  async exportToCSV(device: DeviceData, telemetryData: TelemetryDataPoint[]): Promise<string> {
    try {
      const headers = this.getCSVHeaders(device.deviceType);
      const rows = telemetryData.map((d) => this.formatCSVRow(d, device.deviceType));

      const csv = [headers, ...rows].join('\n');

      const fileName = `${device.name.replace(/\s+/g, '_')}_${
        new Date().toISOString().split('T')[0]
      }.csv`;

      // Handle web differently - use Blob and download
      if ((Platform.OS as any) === 'web') {
        // Create blob and download on web
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return fileName; // Return filename as "uri" for web
      } else {
        // Native platforms
        const file = new File(Paths.document, fileName);
        await file.write(csv);
        return file.uri;
      }
    } catch (error) {
      logger.error('Failed to export CSV:', error);
      throw error;
    }
  }

  /**
   * Share report
   */
  async shareReport(uri: string, fileName: string): Promise<void> {
    try {
      // On web, the file is already downloaded, so just show a success message
      if ((Platform.OS as any) === 'web') {
        logger.info(`Report downloaded: ${fileName}`);
        return;
      }

      // Native platforms
      const isAvailable = await Sharing.isAvailableAsync();
      if (isAvailable) {
        const isCSV = fileName.toLowerCase().endsWith('.csv');
        await Sharing.shareAsync(uri, {
          mimeType: isCSV ? 'text/csv' : 'application/pdf',
          dialogTitle: `Share ${fileName}`,
          UTI: isCSV ? 'public.comma-separated-values-text' : 'com.adobe.pdf',
        });
      } else {
        throw new Error('Sharing is not available');
      }
    } catch (error) {
      logger.error('Failed to share report:', error);
      throw error;
    }
  }

  /**
   * Calculate statistics for telemetry data
   */
  private calculateStatistics(
    data: TelemetryDataPoint[],
    deviceType: DeviceType,
  ): Record<string, Statistics> {
    const stats: Record<string, Statistics> = {};

    const metrics = this.getMetricsForDeviceType(deviceType);

    metrics.forEach((metric) => {
      const values = data
        .map((d) => d[metric as keyof TelemetryDataPoint] as number)
        .filter((v) => v !== undefined && v !== null);

      if (values.length > 0) {
        stats[metric] = {
          min: Math.min(...values),
          max: Math.max(...values),
          avg: values.reduce((a, b) => a + b, 0) / values.length,
          count: values.length,
        };
      }
    });

    return stats;
  }

  /**
   * Get relevant metrics for device type
   */
  private getMetricsForDeviceType(deviceType: DeviceType): string[] {
    const metricsMap: Record<DeviceType, string[]> = {
      [DeviceType.TEMPERATURE]: ['temperature'],
      [DeviceType.HUMIDITY]: ['humidity'],
      [DeviceType.TEMP_HUMIDITY]: ['temperature', 'humidity'],
      [DeviceType.MULTI_SENSOR]: ['temperature', 'humidity'],
      [DeviceType.LEVEL]: ['level'],
      [DeviceType.ENERGY]: ['power', 'energy'],
      [DeviceType.POWER]: ['power'],
      [DeviceType.WATER]: ['level', 'flow'],
      [DeviceType.DIESEL]: ['level', 'temperature'],
      [DeviceType.GAS]: ['level', 'pressure'],
      [DeviceType.DIFFERENTIAL_PRESSURE]: ['pressureDiffPa'],
      [DeviceType.MOTION]: ['motion'],
      [DeviceType.DOOR]: ['status'],
      [DeviceType.CAMERA]: [],
      [DeviceType.ACTUATOR]: ['status'],
      [DeviceType.SENSOR]: ['temperature', 'humidity', 'level'],
    };

    return metricsMap[deviceType] || [];
  }

  /**
   * Generate HTML content for report
   */
  private generateHTML(
    device: DeviceData,
    telemetryData: TelemetryDataPoint[],
    stats: Record<string, Statistics>,
    options: ReportOptions,
    organizationName: string,
    isArabic: boolean,
    logoBase64?: string,
  ): string {
    const direction = isArabic ? 'rtl' : 'ltr';
    const fontFamily = isArabic ? 'Arial, sans-serif' : 'Helvetica, Arial, sans-serif';

    return `
<!DOCTYPE html>
<html dir="${direction}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: ${fontFamily}; 
      padding: 40px; 
      color: #1e293b;
      direction: ${direction};
    }
    .header { 
      border-bottom: 3px solid #3949ab; 
      padding-bottom: 20px; 
      margin-bottom: 30px;
    }
    .header h1 { 
      color: #3949ab; 
      font-size: 28px; 
      margin-bottom: 10px;
    }
    .header .meta { 
      color: #64748b; 
      font-size: 14px;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px;
    }
    .section { 
      margin-bottom: 30px; 
      padding: 20px;
      background: #f8fafc;
      border-radius: 8px;
    }
    .section h2 { 
      color: #334155; 
      font-size: 20px; 
      margin-bottom: 15px;
      border-bottom: 2px solid #e2e8f0;
      padding-bottom: 8px;
    }
    .info-grid { 
      display: grid; 
      grid-template-columns: repeat(2, 1fr); 
      gap: 15px;
    }
    .info-item { 
      display: flex; 
      justify-content: space-between;
      padding: 10px;
      background: white;
      border-radius: 6px;
      border-left: 3px solid #3949ab;
    }
    ${isArabic ? '.info-item { border-left: none; border-right: 3px solid #3949ab; }' : ''}
    .info-label { 
      font-weight: 600; 
      color: #475569;
    }
    .info-value { 
      color: #1e293b;
      font-weight: 500;
    }
    .stats-grid { 
      display: grid; 
      grid-template-columns: repeat(4, 1fr); 
      gap: 15px;
    }
    .stat-card { 
      background: white; 
      padding: 15px; 
      border-radius: 8px;
      text-align: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .stat-label { 
      font-size: 12px; 
      color: #64748b; 
      text-transform: uppercase;
      margin-bottom: 5px;
    }
    .stat-value { 
      font-size: 24px; 
      font-weight: 700; 
      color: #3949ab;
    }
    .stat-unit { 
      font-size: 14px; 
      color: #64748b;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      background: white;
      border-radius: 8px;
      overflow: hidden;
    }
    th { 
      background: #3949ab; 
      color: white; 
      padding: 12px; 
      text-align: ${isArabic ? 'right' : 'left'};
      font-weight: 600;
    }
    td { 
      padding: 10px 12px; 
      border-bottom: 1px solid #e2e8f0;
      text-align: ${isArabic ? 'right' : 'left'};
    }
    tr:hover { background: #f8fafc; }
    .footer { 
      margin-top: 40px; 
      padding-top: 20px; 
      border-top: 2px solid #e2e8f0;
      text-align: center; 
      color: #64748b; 
      font-size: 12px;
    }
    .badge { 
      display: inline-block; 
      padding: 4px 12px; 
      border-radius: 12px; 
      font-size: 12px; 
      font-weight: 600;
      background: #dcfce7;
      color: #166534;
    }
    @media print {
      body { padding: 20px; }
      .section { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div style="display: flex; align-items: center; gap: 14px; margin-bottom: 10px;">
      ${
        logoBase64
          ? `<img src="data:image/png;base64,${logoBase64}" style="height: 48px; width: auto; object-fit: contain;" alt="Logo">`
          : ''
      }
      <h1 style="margin: 0;">${isArabic ? 'تقرير الجهاز' : 'Device Report'}</h1>
    </div>
    <div class="meta">
      <span><strong>${isArabic ? 'المنظمة:' : 'Organization:'}</strong> ${organizationName}</span>
      <span><strong>${isArabic ? 'التاريخ:' : 'Date:'}</strong> ${new Date().toLocaleDateString(
      isArabic ? 'ar-SA' : 'en-US',
    )}</span>
      <span><strong>${isArabic ? 'الفترة:' : 'Period:'}</strong> ${options.dateRange}</span>
    </div>
  </div>

  <div class="section">
    <h2>${isArabic ? 'معلومات الجهاز' : 'Device Information'}</h2>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">${isArabic ? 'الاسم' : 'Name'}</span>
        <span class="info-value">${device.name}</span>
      </div>
      <div class="info-item">
        <span class="info-label">${isArabic ? 'النوع' : 'Type'}</span>
        <span class="info-value">${device.deviceType}</span>
      </div>
      ${
        device.site
          ? `
      <div class="info-item">
        <span class="info-label">${isArabic ? 'الموقع' : 'Site'}</span>
        <span class="info-value">${device.site}</span>
      </div>`
          : ''
      }
      ${
        device.zone
          ? `
      <div class="info-item">
        <span class="info-label">${isArabic ? 'المنطقة' : 'Zone'}</span>
        <span class="info-value">${device.zone}</span>
      </div>`
          : ''
      }
      <div class="info-item">
        <span class="info-label">${isArabic ? 'الحالة' : 'Status'}</span>
        <span class="info-value"><span class="badge">${(() => {
          const ls = device.lastSeenAt;
          if (ls) {
            return new Date().getTime() - new Date(ls).getTime() < 15 * 60 * 1000
              ? isArabic
                ? 'متصل'
                : 'Online'
              : isArabic
              ? 'غير متصل'
              : 'Offline';
          }
          const st = (device.status || '').toLowerCase();
          return ['online', 'active', 'connected'].includes(st)
            ? isArabic
              ? 'متصل'
              : 'Online'
            : isArabic
            ? 'غير متصل'
            : 'Offline';
        })()}</span></span>
      </div>
      ${
        device.hasBattery
          ? `
      <div class="info-item">
        <span class="info-label">${isArabic ? 'مصدر الطاقة' : 'Power Source'}</span>
        <span class="info-value">${isArabic ? 'بطارية' : 'Battery'}</span>
      </div>`
          : ''
      }
    </div>
  </div>

  ${options.includeStatistics ? this.generateStatisticsHTML(stats, isArabic) : ''}
  
  ${
    options.includeCharts ? this.generateChartsHTML(telemetryData, device.deviceType, isArabic) : ''
  }
  
  ${
    options.includeRawData
      ? this.generateDataTableHTML(telemetryData, device.deviceType, isArabic)
      : ''
  }

  <div class="footer">
    <p>${
      isArabic ? 'تم إنشاء هذا التقرير بواسطة IoT Edge Platform' : 'Generated by IoT Edge Platform'
    }</p>
    <p>${new Date().toLocaleString(isArabic ? 'ar-SA' : 'en-US')}</p>
  </div>
</body>
</html>
    `;
  }

  /**
   * Generate dynamic HTML using device type configuration
   * This method creates reports based on database-driven templates
   */
  private generateDynamicHTML(
    device: DeviceData & { capabilities?: Record<string, any> },
    deviceTypeConfig: DeviceTypeConfig,
    reportData: any,
    options: ReportOptions,
    organizationName: string,
    isArabic: boolean,
  ): string {
    const direction = isArabic ? 'rtl' : 'ltr';
    const fontFamily = isArabic ? 'Arial, sans-serif' : 'Helvetica, Arial, sans-serif';
    const primaryColor = deviceTypeConfig.colorPrimary || '#3949ab';
    const secondaryColor = deviceTypeConfig.colorSecondary || '#283593';

    return `
<!DOCTYPE html>
<html dir="${direction}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${isArabic ? 'تقرير الجهاز' : 'Device Report'} - ${device.name}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: ${fontFamily}; 
      padding: 40px; 
      background: #f8fafc; 
      color: #1e293b;
      direction: ${direction};
    }
    .header { 
      background: linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%);
      color: white; 
      padding: 30px; 
      border-radius: 12px; 
      margin-bottom: 30px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    .header h1 { 
      font-size: 28px; 
      margin-bottom: 15px;
      font-weight: 700;
    }
    .meta { 
      display: flex; 
      gap: 20px; 
      flex-wrap: wrap;
      font-size: 14px;
      opacity: 0.95;
    }
    .section { 
      background: white; 
      padding: 25px; 
      margin-bottom: 20px; 
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }
    .section h2 { 
      color: ${primaryColor}; 
      margin-bottom: 20px; 
      font-size: 20px; 
      font-weight: 600;
      padding-bottom: 10px;
      border-bottom: 2px solid #e2e8f0;
    }
    .device-info { 
      display: grid; 
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
      gap: 15px;
    }
    .info-item { 
      display: flex; 
      flex-direction: column;
      gap: 5px;
    }
    .info-label { 
      color: #64748b; 
      font-size: 13px; 
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .info-value { 
      color: #1e293b; 
      font-size: 16px; 
      font-weight: 500;
    }
    .stats-grid { 
      display: grid; 
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); 
      gap: 15px;
    }
    .stat-card { 
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); 
      padding: 15px; 
      border-radius: 8px;
      border: 1px solid #e2e8f0;
    }
    .stat-label { 
      color: #64748b; 
      font-size: 12px; 
      font-weight: 600;
      margin-bottom: 8px;
    }
    .stat-value { 
      color: ${primaryColor}; 
      font-size: 24px; 
      font-weight: 700;
    }
    .stat-unit { 
      font-size: 14px; 
      color: #64748b; 
      font-weight: 500;
      margin-${isArabic ? 'right' : 'left'}: 4px;
    }
    table { 
      width: 100%; 
      border-collapse: collapse;
      background: white;
      border-radius: 8px;
      overflow: hidden;
    }
    th { 
      background: ${primaryColor}; 
      color: white; 
      padding: 12px; 
      text-align: ${isArabic ? 'right' : 'left'};
      font-weight: 600;
    }
    td { 
      padding: 10px 12px; 
      border-bottom: 1px solid #e2e8f0;
      text-align: ${isArabic ? 'right' : 'left'};
    }
    tr:hover { background: #f8fafc; }
    .footer { 
      margin-top: 40px; 
      padding-top: 20px; 
      border-top: 2px solid #e2e8f0;
      text-align: center; 
      color: #64748b; 
      font-size: 12px;
    }
    .badge { 
      display: inline-block; 
      padding: 4px 12px; 
      border-radius: 12px; 
      font-size: 12px; 
      font-weight: 600;
      background: #dcfce7;
      color: #166534;
    }
    @media print {
      body { padding: 20px; }
      .section { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${isArabic ? 'تقرير الجهاز' : 'Device Report'}</h1>
    <div class="meta">
      <span><strong>${isArabic ? 'المنظمة:' : 'Organization:'}</strong> ${organizationName}</span>
      <span><strong>${isArabic ? 'التاريخ:' : 'Date:'}</strong> ${new Date().toLocaleDateString(
      isArabic ? 'ar-SA' : 'en-US',
    )}</span>
      <span><strong>${isArabic ? 'الفترة:' : 'Period:'}</strong> ${options.dateRange}</span>
      <span><strong>${isArabic ? 'النوع:' : 'Type:'}</strong> ${
      isArabic ? deviceTypeConfig.labelAr : deviceTypeConfig.labelEn
    }</span>
    </div>
  </div>

  <div class="section">
    <h2>${isArabic ? 'معلومات الجهاز' : 'Device Information'}</h2>
    <div class="device-info">
      <div class="info-item">
        <span class="info-label">${isArabic ? 'اسم الجهاز' : 'Device Name'}</span>
        <span class="info-value">${device.name}</span>
      </div>
      <div class="info-item">
        <span class="info-label">${isArabic ? 'النوع' : 'Type'}</span>
        <span class="info-value">${
          isArabic ? deviceTypeConfig.labelAr : deviceTypeConfig.labelEn
        }</span>
      </div>
      ${
        device.site
          ? `
      <div class="info-item">
        <span class="info-label">${isArabic ? 'الموقع' : 'Site'}</span>
        <span class="info-value">${device.site}</span>
      </div>`
          : ''
      }
      ${
        device.zone
          ? `
      <div class="info-item">
        <span class="info-label">${isArabic ? 'المنطقة' : 'Zone'}</span>
        <span class="info-value">${device.zone}</span>
      </div>`
          : ''
      }
      <div class="info-item">
        <span class="info-label">${isArabic ? 'الحالة' : 'Status'}</span>
        <span class="info-value"><span class="badge">${(() => {
          const ls = device.lastSeenAt;
          if (ls) {
            return new Date().getTime() - new Date(ls).getTime() < 15 * 60 * 1000
              ? isArabic
                ? 'متصل'
                : 'Online'
              : isArabic
              ? 'غير متصل'
              : 'Offline';
          }
          const st = (device.status || '').toLowerCase();
          return ['online', 'active', 'connected'].includes(st)
            ? isArabic
              ? 'متصل'
              : 'Online'
            : isArabic
            ? 'غير متصل'
            : 'Offline';
        })()}</span></span>
      </div>
      ${
        deviceTypeConfig.supportsBattery
          ? `
      <div class="info-item">
        <span class="info-label">${isArabic ? 'مصدر الطاقة' : 'Power Source'}</span>
        <span class="info-value">${isArabic ? 'بطارية' : 'Battery'}</span>
      </div>`
          : ''
      }
    </div>
  </div>

  ${
    options.includeStatistics && reportData.sections
      ? this.generateDynamicStatisticsHTML(reportData.sections, isArabic, primaryColor)
      : ''
  }

  <div class="footer">
    <p>${
      isArabic ? 'تم إنشاء هذا التقرير بواسطة IoT Edge Platform' : 'Generated by IoT Edge Platform'
    }</p>
    <p>${new Date().toLocaleString(isArabic ? 'ar-SA' : 'en-US')}</p>
  </div>
</body>
</html>
    `;
  }

  /**
   * Generate dynamic statistics HTML from report sections
   */
  private generateDynamicStatisticsHTML(
    sections: any[],
    isArabic: boolean,
    primaryColor: string,
  ): string {
    if (!sections || sections.length === 0) return '';

    return sections
      .map(
        (section) => `
  <div class="section">
    <h2>${isArabic ? section.titleAr : section.title}</h2>
    <div class="stats-grid">
      ${section.metrics
        .map(
          (metric: any) => `
        <div class="stat-card">
          <div class="stat-label">${
            isArabic ? getMetricLabel(metric.name, true) : getMetricLabel(metric.name, false)
          }</div>
          <div class="stat-value">${formatMetricValue(metric.name, metric.value)}</div>
        </div>
      `,
        )
        .join('')}
    </div>
  </div>
    `,
      )
      .join('');
  }

  /**
   * Generate statistics section HTML
   */
  private generateStatisticsHTML(stats: Record<string, Statistics>, isArabic: boolean): string {
    const entries = Object.entries(stats);
    if (entries.length === 0) return '';

    return `
  <div class="section">
    <h2>${isArabic ? 'الإحصائيات' : 'Statistics'}</h2>
    <div class="stats-grid">
      ${entries
        .map(
          ([metric, stat]) => `
        <div class="stat-card">
          <div class="stat-label">${
            isArabic ? this.getMetricLabelAr(metric) : this.getMetricLabel(metric)
          }</div>
          <div class="stat-value">${stat.avg.toFixed(
            1,
          )}<span class="stat-unit">${this.getMetricUnit(metric)}</span></div>
          <div style="font-size: 11px; color: #64748b; margin-top: 5px;">
            ${isArabic ? 'أدنى' : 'Min'}: ${stat.min.toFixed(1)} | ${
            isArabic ? 'أعلى' : 'Max'
          }: ${stat.max.toFixed(1)}
          </div>
        </div>
      `,
        )
        .join('')}
    </div>
  </div>
    `;
  }

  /**
   * Generate charts section HTML with SVG line charts
   */
  private generateChartsHTML(
    data: TelemetryDataPoint[],
    deviceType: DeviceType,
    isArabic: boolean,
  ): string {
    const metrics = this.getMetricsForDeviceType(deviceType);
    if (data.length === 0 || metrics.length === 0) return '';

    // Take last 24 data points for better visualization
    const chartData = data.slice(-24);

    return `
  <div class="section">
    <h2>${isArabic ? 'الرسوم البيانية' : 'Charts'}</h2>
    ${metrics.map((metric) => this.generateLineChart(chartData, metric, isArabic)).join('')}
  </div>
    `;
  }

  /**
   * Generate SVG line chart for a metric
   */
  private generateLineChart(data: TelemetryDataPoint[], metric: string, isArabic: boolean): string {
    const values = data
      .map((d) => d[metric as keyof TelemetryDataPoint] as number)
      .filter((v) => v !== undefined && v !== null);

    if (values.length === 0) return '';

    const width = 700;
    const height = 250;
    const padding = { top: 40, right: 50, bottom: 50, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue || 1;

    // Calculate points for the line
    const points = values
      .map((value, index) => {
        const x = padding.left + (index / (values.length - 1 || 1)) * chartWidth;
        const y = padding.top + chartHeight - ((value - minValue) / valueRange) * chartHeight;
        return `${x},${y}`;
      })
      .join(' ');

    // Create area under the line
    const areaPoints = `${padding.left},${padding.top + chartHeight} ${points} ${
      padding.left + chartWidth
    },${padding.top + chartHeight}`;

    // Grid lines
    const gridLines = [0, 25, 50, 75, 100]
      .map((percent) => {
        const y = padding.top + (chartHeight * percent) / 100;
        const value = maxValue - (valueRange * percent) / 100;
        return `
        <line x1="${padding.left}" y1="${y}" x2="${padding.left + chartWidth}" y2="${y}" 
              stroke="#e2e8f0" stroke-width="1" stroke-dasharray="4,4"/>
        <text x="${padding.left - 10}" y="${y + 4}" text-anchor="end" 
              fill="#64748b" font-size="11">${value.toFixed(1)}</text>
      `;
      })
      .join('');

    return `
    <div style="margin-bottom: 30px; background: white; padding: 20px; border-radius: 8px;">
      <h3 style="color: #334155; font-size: 16px; margin-bottom: 15px;">
        ${isArabic ? this.getMetricLabelAr(metric) : this.getMetricLabel(metric)} 
        <span style="color: #64748b; font-size: 12px; font-weight: normal;">
          (${this.getMetricUnit(metric)})
        </span>
      </h3>
      <svg width="${width}" height="${height}" style="display: block; margin: 0 auto;">
        <!-- Background -->
        <rect x="${padding.left}" y="${padding.top}" 
              width="${chartWidth}" height="${chartHeight}" 
              fill="#f8fafc" rx="4"/>
        
        <!-- Grid lines -->
        ${gridLines}
        
        <!-- Area under line -->
        <polygon points="${areaPoints}" 
                 fill="#3949ab" fill-opacity="0.1"/>
        
        <!-- Line -->
        <polyline points="${points}" 
                  fill="none" stroke="#3949ab" stroke-width="3" 
                  stroke-linecap="round" stroke-linejoin="round"/>
        
        <!-- Data points -->
        ${values
          .map((value, index) => {
            const x = padding.left + (index / (values.length - 1 || 1)) * chartWidth;
            const y = padding.top + chartHeight - ((value - minValue) / valueRange) * chartHeight;
            return `
            <circle cx="${x}" cy="${y}" r="4" fill="white" stroke="#3949ab" stroke-width="2"/>
            <circle cx="${x}" cy="${y}" r="2" fill="#3949ab"/>
          `;
          })
          .join('')}
        
        <!-- Axes -->
        <line x1="${padding.left}" y1="${padding.top}" 
              x2="${padding.left}" y2="${padding.top + chartHeight}" 
              stroke="#94a3b8" stroke-width="2"/>
        <line x1="${padding.left}" y1="${padding.top + chartHeight}" 
              x2="${padding.left + chartWidth}" y2="${padding.top + chartHeight}" 
              stroke="#94a3b8" stroke-width="2"/>
        
        <!-- X-axis labels -->
        ${[0, Math.floor(values.length / 2), values.length - 1]
          .map((index) => {
            if (index >= values.length) return '';
            const x = padding.left + (index / (values.length - 1 || 1)) * chartWidth;
            const timestamp = data[data.length - values.length + index]?.timestamp;
            const label = timestamp
              ? new Date(timestamp).toLocaleTimeString(isArabic ? 'ar-SA' : 'en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '';
            return `
            <text x="${x}" y="${padding.top + chartHeight + 25}" 
                  text-anchor="middle" fill="#64748b" font-size="11">${label}</text>
          `;
          })
          .join('')}
        
        <!-- Y-axis label -->
        <text x="${padding.left - 45}" y="${padding.top + chartHeight / 2}" 
              text-anchor="middle" fill="#475569" font-size="12" font-weight="600"
              transform="rotate(-90, ${padding.left - 45}, ${padding.top + chartHeight / 2})">
          ${
            isArabic ? this.getMetricLabelAr(metric) : this.getMetricLabel(metric)
          } (${this.getMetricUnit(metric)})
        </text>
        
        <!-- Stats box -->
        <rect x="${width - padding.right - 140}" y="${padding.top + 10}" 
              width="130" height="80" fill="white" rx="4" 
              stroke="#e2e8f0" stroke-width="1"/>
        <text x="${width - padding.right - 135}" y="${padding.top + 30}" 
              fill="#475569" font-size="11" font-weight="600">
          ${isArabic ? 'الإحصائيات' : 'Statistics'}
        </text>
        <text x="${width - padding.right - 135}" y="${padding.top + 48}" 
              fill="#64748b" font-size="10">
          ${isArabic ? 'أعلى' : 'Max'}: ${maxValue.toFixed(1)}${this.getMetricUnit(metric)}
        </text>
        <text x="${width - padding.right - 135}" y="${padding.top + 63}" 
              fill="#64748b" font-size="10">
          ${isArabic ? 'متوسط' : 'Avg'}: ${(
      values.reduce((a, b) => a + b, 0) / values.length
    ).toFixed(1)}${this.getMetricUnit(metric)}
        </text>
        <text x="${width - padding.right - 135}" y="${padding.top + 78}" 
              fill="#64748b" font-size="10">
          ${isArabic ? 'أدنى' : 'Min'}: ${minValue.toFixed(1)}${this.getMetricUnit(metric)}
        </text>
      </svg>
    </div>
    `;
  }

  /**
   * Generate data table HTML
   */
  private generateDataTableHTML(
    data: TelemetryDataPoint[],
    deviceType: DeviceType,
    isArabic: boolean,
  ): string {
    const metrics = this.getMetricsForDeviceType(deviceType);
    if (data.length === 0) return '';

    const recentData = data.slice(-50); // Last 50 readings

    return `
  <div class="section">
    <h2>${isArabic ? 'القراءات الأخيرة' : 'Recent Readings'}</h2>
    <table>
      <thead>
        <tr>
          <th>${isArabic ? 'التاريخ والوقت' : 'Timestamp'}</th>
          ${metrics
            .map((m) => `<th>${isArabic ? this.getMetricLabelAr(m) : this.getMetricLabel(m)}</th>`)
            .join('')}
          ${data.some((d) => d.rssi) ? `<th>${isArabic ? 'الإشارة' : 'Signal'}</th>` : ''}
        </tr>
      </thead>
      <tbody>
        ${recentData
          .map(
            (d) => `
          <tr>
            <td>${new Date(d.timestamp).toLocaleString(isArabic ? 'ar-SA' : 'en-US')}</td>
            ${metrics
              .map((m) => {
                const value = d[m as keyof TelemetryDataPoint];
                return `<td>${
                  value !== undefined && value !== null
                    ? `${(value as number).toFixed(1)}${this.getMetricUnit(m)}`
                    : '-'
                }</td>`;
              })
              .join('')}
            ${d.rssi ? `<td>${d.rssi} dBm</td>` : ''}
          </tr>
        `,
          )
          .join('')}
      </tbody>
    </table>
  </div>
    `;
  }

  /**
   * Generate comparison report HTML
   */
  private generateComparisonHTML(
    devices: DeviceData[],
    telemetryDataMap: Map<string, TelemetryDataPoint[]>,
    options: ReportOptions,
    organizationName: string,
    isArabic: boolean,
  ): string {
    // Similar structure but compares multiple devices
    return `<!DOCTYPE html>
<html dir="${isArabic ? 'rtl' : 'ltr'}">
<head>
  <meta charset="UTF-8">
  <title>${isArabic ? 'تقرير مقارنة' : 'Comparison Report'}</title>
</head>
<body>
  <h1>${isArabic ? 'تقرير مقارنة الأجهزة' : 'Device Comparison Report'}</h1>
  <p>${isArabic ? 'قريباً' : 'Coming soon'}</p>
</body>
</html>`;
  }

  /**
   * Get metric label in English
   */
  private getMetricLabel(metric: string): string {
    const labels: Record<string, string> = {
      temperature: 'Temperature',
      humidity: 'Humidity',
      level: 'Level',
      battery: 'Battery',
      rssi: 'Signal',
      pressure: 'Pressure',
      light: 'Light',
      co2: 'CO2',
      pm25: 'PM2.5',
      power: 'Power',
      energy: 'Energy',
      flow: 'Flow',
    };
    return labels[metric] || metric;
  }

  /**
   * Get metric label in Arabic
   */
  private getMetricLabelAr(metric: string): string {
    const labels: Record<string, string> = {
      temperature: 'الحرارة',
      humidity: 'الرطوبة',
      level: 'المستوى',
      battery: 'البطارية',
      rssi: 'الإشارة',
      pressure: 'الضغط',
      light: 'الإضاءة',
      co2: 'ثاني أكسيد الكربون',
      pm25: 'الجسيمات الدقيقة',
      power: 'الطاقة',
      energy: 'الاستهلاك',
      flow: 'التدفق',
    };
    return labels[metric] || metric;
  }

  /**
   * Get metric unit
   */
  private getMetricUnit(metric: string): string {
    const units: Record<string, string> = {
      temperature: '°C',
      humidity: '%',
      level: 'cm',
      battery: 'V',
      rssi: 'dBm',
      pressure: 'kPa',
      light: 'lux',
      co2: 'ppm',
      pm25: 'µg/m³',
      power: 'W',
      energy: 'kWh',
      flow: 'L/min',
    };
    return units[metric] || '';
  }

  /**
   * Get CSV headers
   */
  private getCSVHeaders(deviceType: DeviceType): string {
    const metrics = this.getMetricsForDeviceType(deviceType);
    return ['Timestamp', ...metrics.map((m) => this.getMetricLabel(m)), 'Signal (dBm)'].join(',');
  }

  /**
   * Format CSV row
   */
  private formatCSVRow(data: TelemetryDataPoint, deviceType: DeviceType): string {
    const metrics = this.getMetricsForDeviceType(deviceType);
    const values = [
      new Date(data.timestamp).toISOString(),
      ...metrics.map((m) => {
        const value = data[m as keyof TelemetryDataPoint];
        return value !== undefined && value !== null ? value : '';
      }),
      data.rssi || '',
    ];
    return values.join(',');
  }
}

export default new ReportGeneratorService();
